import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Robot, Bell, ChartBar, Warning, CheckCircle, XCircle, Clock,
  ArrowClockwise, FileText, Download, ChatCircle, PaperPlaneTilt,
  CaretDown, CaretUp, Lightning, Eye, Siren, Users, Ticket,
  WifiHigh, CurrencyDollar, Wrench, Path, Megaphone, UsersThree,
  MapTrifold, ShieldCheck, ClipboardText, X, ArrowRight, Spinner,
  TrendUp, TrendDown, Minus, MagnifyingGlass
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEPT_ICONS = {
  'NOC': ShieldCheck,
  'Fiber Map': MapTrifold,
  'Customers': Users,
  'Tickets': Ticket,
  'Sales': Megaphone,
  'HR': UsersThree,
  'Finance': CurrencyDollar,
  'BMFT': Path,
  'IMFT': Wrench,
  'Service Ops': ClipboardText,
};

const DEPT_COLORS = {
  'NOC': '#3b82f6',
  'Fiber Map': '#8b5cf6',
  'Customers': '#06b6d4',
  'Tickets': '#f59e0b',
  'Sales': '#10b981',
  'HR': '#ec4899',
  'Finance': '#14b8a6',
  'BMFT': '#f97316',
  'IMFT': '#6366f1',
  'Service Ops': '#84cc16',
};

const SEVERITY_CONFIG = {
  critical: { color: '#ef4444', bg: '#fef2f2', border: '#fecaca', label: 'Critical', icon: Siren },
  warning: { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', label: 'Warning', icon: Warning },
  info: { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', label: 'Info', icon: Bell },
};

function ScoreRing({ score, size = 60 }) {
  const r = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const dash = (pct / 100) * circ;
  const color = pct >= 80 ? '#22c55e' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
        strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round" />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        style={{ transform: 'rotate(90deg)', transformOrigin: `${size/2}px ${size/2}px`,
          fontSize: size > 50 ? '13px' : '10px', fontWeight: 700, fill: color }}>
        {Math.round(pct)}
      </text>
    </svg>
  );
}

function DeptCard({ dept, score, alerts }) {
  const Icon = DEPT_ICONS[dept] || ChartBar;
  const color = DEPT_COLORS[dept] || '#6b7280';
  const status = score >= 80 ? 'Normal' : score >= 60 ? 'Warning' : 'Critical';
  const statusColor = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  const deptAlerts = alerts.filter(a => a.department === dept && !a.resolved);
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '16px',
      border: `1px solid ${score < 60 ? '#fecaca' : score < 80 ? '#fde68a' : '#e5e7eb'}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', gap: 8
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: color + '20',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={18} color={color} weight="bold" />
          </div>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{dept}</span>
        </div>
        <ScoreRing score={score} size={44} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
        <span style={{ fontSize: 12, color: statusColor, fontWeight: 600 }}>{status}</span>
        {deptAlerts.length > 0 && (
          <span style={{ marginLeft: 'auto', background: score < 60 ? '#fef2f2' : '#fffbeb',
            color: score < 60 ? '#ef4444' : '#f59e0b', fontSize: 11, fontWeight: 700,
            padding: '2px 7px', borderRadius: 20 }}>
            {deptAlerts.length} alert{deptAlerts.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

function AlertCard({ alert, onResolve, onSnooze, onEscalate }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  const Icon = cfg.icon;
  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };
  return (
    <div style={{
      background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10,
      padding: '12px 14px', marginBottom: 8
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Icon size={18} color={cfg.color} weight="fill" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>{alert.title}</span>
            <span style={{ fontSize: 11, background: cfg.color + '20', color: cfg.color,
              padding: '1px 7px', borderRadius: 20, fontWeight: 600 }}>{alert.department}</span>
            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>{timeAgo(alert.created_at)}</span>
          </div>
          <p style={{ fontSize: 12, color: '#4b5563', margin: '4px 0 0', lineHeight: 1.5 }}>{alert.description}</p>
          {expanded && (
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff',
              borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <Lightning size={14} color="#f59e0b" weight="fill" style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>AI Suggestion: </span>
                  <span style={{ fontSize: 12, color: '#4b5563' }}>{alert.suggestion}</span>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setExpanded(!expanded)}
              style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db',
                background: '#fff', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: 3 }}>
              {expanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
              {expanded ? 'Hide' : 'AI Suggestion'}
            </button>
            <button onClick={() => onResolve(alert._id)}
              style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none',
                background: '#22c55e', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: 3 }}>
              <CheckCircle size={11} /> Resolve
            </button>
            <button onClick={() => onSnooze(alert._id)}
              style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db',
                background: '#fff', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Clock size={11} /> Snooze 1h
            </button>
            {alert.severity !== 'critical' && (
              <button onClick={() => onEscalate(alert._id)}
                style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none',
                  background: '#ef4444', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Siren size={11} /> Escalate
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatPanel({ token }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I am your AI Manager assistant. I have access to live ERP data. Ask me anything about your departments, staff, customers, tickets, or network status.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const history = messages.slice(-6).map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/api/ai-manager/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ message: userMsg, history })
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response || 'Sorry, I could not process that.' }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const QUICK_QUESTIONS = [
    'How many customers went offline today?',
    'Which technician has the most pending tickets?',
    'Show me today\'s staff attendance summary',
    'What are the critical network alerts?',
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', borderRadius: 12,
      border: '1px solid #e5e7eb', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
        display: 'flex', alignItems: 'center', gap: 8 }}>
        <Robot size={20} color="#fff" weight="fill" />
        <span style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>AI Manager Assistant</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#93c5fd', background: '#1e40af',
          padding: '2px 8px', borderRadius: 20 }}>Claude AI • Live Data</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '85%', padding: '10px 12px', borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
              background: msg.role === 'user' ? '#2563eb' : '#f3f4f6',
              color: msg.role === 'user' ? '#fff' : '#1f2937',
              fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap'
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ background: '#f3f4f6', borderRadius: '12px 12px 12px 2px', padding: '10px 14px' }}>
              <Spinner size={16} color="#6b7280" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {messages.length <= 1 && (
        <div style={{ padding: '0 12px 8px' }}>
          <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Quick questions:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {QUICK_QUESTIONS.map((q, i) => (
              <button key={i} onClick={() => { setInput(q); }}
                style={{ textAlign: 'left', fontSize: 11, padding: '5px 8px', borderRadius: 6,
                  border: '1px solid #e5e7eb', background: '#f9fafb', cursor: 'pointer', color: '#374151' }}>
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Ask about any department..."
          style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
            fontSize: 13, outline: 'none', background: '#f9fafb' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '8px 12px', borderRadius: 8, border: 'none',
            background: loading || !input.trim() ? '#d1d5db' : '#2563eb',
            cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center' }}>
          <PaperPlaneTilt size={16} color="#fff" weight="fill" />
        </button>
      </div>
    </div>
  );
}

function ReportCard({ report, onView }) {
  const typeColor = report.type === 'daily' ? '#3b82f6' : '#8b5cf6';
  const score = report.metrics?.avg_health_score || 0;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px',
      display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'box-shadow 0.2s' }}
      onClick={() => onView(report)} onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
      <div style={{ width: 40, height: 40, borderRadius: 8, background: typeColor + '15',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <FileText size={20} color={typeColor} weight="fill" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: '#1f2937', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis' }}>{report.title}</p>
        <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>
          {new Date(report.created_at).toLocaleString()} · Health: {score}/100
        </p>
      </div>
      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
        background: typeColor + '15', color: typeColor }}>
        {report.type}
      </span>
      <ArrowRight size={14} color="#9ca3af" />
    </div>
  );
}

export default function AIManager() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('overview');
  const [healthData, setHealthData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [alertFilter, setAlertFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [downNudges, setDownNudges] = useState(null);
  const [billingNudges, setBillingNudges] = useState(null);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [expandedNudge, setExpandedNudge] = useState(null);
  const [salesNudges, setSalesNudges] = useState(null);
  const [hrNudges, setHrNudges] = useState(null);
  const refreshInterval = useRef(null);

  const fetchAll = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [healthRes, alertsRes, statsRes, reportsRes] = await Promise.all([
        fetch(`${API}/api/ai-manager/health`, { headers }),
        fetch(`${API}/api/ai-manager/alerts?resolved=false&limit=50`, { headers }),
        fetch(`${API}/api/ai-manager/stats`, { headers }),
        fetch(`${API}/api/ai-manager/reports?limit=10`, { headers }),
      ]);
      if (healthRes.ok) setHealthData(await healthRes.json());
      if (alertsRes.ok) {
        const d = await alertsRes.json();
        setAlerts(d.alerts || []);
        setUnreadCount(d.unread_count || 0);
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (reportsRes.ok) {
        const d = await reportsRes.json();
        setReports(d.reports || []);
      }
      setLastUpdated(new Date());
    } catch (e) {
      console.error('AI Manager fetch error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAll();
    refreshInterval.current = setInterval(() => fetchAll(), 5 * 60 * 1000);
    return () => clearInterval(refreshInterval.current);
  }, [fetchAll]);

  const handleResolve = async (id) => {
    await fetch(`${API}/api/ai-manager/alerts/${id}/resolve`, {
      method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
    });
    setAlerts(prev => prev.filter(a => a._id !== id));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleSnooze = async (id) => {
    await fetch(`${API}/api/ai-manager/alerts/${id}/snooze`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ hours: 1 })
    });
    setAlerts(prev => prev.filter(a => a._id !== id));
  };

  const handleEscalate = async (id) => {
    await fetch(`${API}/api/ai-manager/alerts/${id}/escalate`, {
      method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
    });
    setAlerts(prev => prev.map(a => a._id === id ? { ...a, severity: 'critical' } : a));
  };

  const generateReport = async (type) => {
    setGeneratingReport(true);
    try {
      const res = await fetch(`${API}/api/ai-manager/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type })
      });
      if (res.ok) {
        const report = await res.json();
        setReports(prev => [report, ...prev]);
        setSelectedReport(report);
        setActiveTab('reports');
      }
    } catch (e) {
      console.error('Report generation error:', e);
    } finally {
      setGeneratingReport(false);
    }
  };

  const fetchNudges = async () => {
    setNudgeLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [dnRes, bnRes, snRes, hrRes] = await Promise.all([
        fetch(`${API}/api/ai-manager/down-nudge`, { headers }),
        fetch(`${API}/api/ai-manager/billing-nudge`, { headers }),
        fetch(`${API}/api/ai-manager/sales-nudge`, { headers }),
        fetch(`${API}/api/ai-manager/hr-nudge`, { headers }),
      ]);
      if (dnRes.ok) setDownNudges(await dnRes.json());
      if (bnRes.ok) setBillingNudges(await bnRes.json());
      if (snRes.ok) setSalesNudges(await snRes.json());
      if (hrRes.ok) setHrNudges(await hrRes.json());
    } catch (e) { console.error('Nudge fetch error:', e); }
    finally { setNudgeLoading(false); }
  };
  const filteredAlerts = alertFilter === 'all' ? alerts
    : alerts.filter(a => a.severity === alertFilter);

  const deptScores = healthData?.dept_scores || {};
  const allDepts = ['NOC', 'Fiber Map', 'Customers', 'Tickets', 'Sales', 'HR', 'Finance', 'BMFT', 'IMFT', 'Service Ops'];
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;
  const avgScore = Object.values(deptScores).length > 0
    ? Math.round(Object.values(deptScores).reduce((a, b) => a + b, 0) / Object.values(deptScores).length)
    : 0;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16 }}>
        <Robot size={48} color="#2563eb" weight="fill" style={{ animation: 'pulse 2s infinite' }} />
        <p style={{ color: '#6b7280', fontSize: 14 }}>AI Manager is analyzing all departments...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto', fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Robot size={24} color="#fff" weight="fill" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1f2937' }}>AI Manager</h1>
            <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>
              {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Monitoring all departments'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {unreadCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fef2f2',
              border: '1px solid #fecaca', borderRadius: 20, padding: '4px 12px' }}>
              <Bell size={14} color="#ef4444" weight="fill" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#ef4444' }}>{unreadCount} alerts</span>
            </div>
          )}
          <button onClick={() => fetchAll(true)} disabled={refreshing}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
              border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151' }}>
            <ArrowClockwise size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button onClick={() => generateReport('daily')} disabled={generatingReport}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
              border: 'none', background: '#2563eb', cursor: 'pointer', fontSize: 13, color: '#fff' }}>
            <FileText size={14} />
            {generatingReport ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Overall Health', value: `${avgScore}%`, icon: ChartBar, color: avgScore >= 80 ? '#22c55e' : avgScore >= 60 ? '#f59e0b' : '#ef4444', bg: '#f0fdf4' },
          { label: 'Critical Alerts', value: criticalCount, icon: Siren, color: '#ef4444', bg: '#fef2f2' },
          { label: 'Warnings', value: warningCount, icon: Warning, color: '#f59e0b', bg: '#fffbeb' },
          { label: 'Resolved Today', value: stats?.resolved_today || 0, icon: CheckCircle, color: '#22c55e', bg: '#f0fdf4' },
          { label: 'Reports This Week', value: stats?.reports_this_week || 0, icon: FileText, color: '#3b82f6', bg: '#eff6ff' },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} style={{ background: s.bg, border: `1px solid ${s.color}30`, borderRadius: 10,
              padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon size={22} color={s.color} weight="fill" />
              <div>
                <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</p>
                <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
        {[
          { id: 'overview', label: 'Department Overview', icon: ChartBar },
          { id: 'alerts', label: `Alerts (${alerts.length})`, icon: Bell },
          { id: 'reports', label: 'Reports', icon: FileText },
          { id: 'chat', label: 'AI Chat', icon: ChatCircle },
          { id: 'nudges', label: 'Smart Nudges', icon: Lightning },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id === 'nudges' && !downNudges) fetchNudges(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                marginBottom: -2, background: 'transparent', cursor: 'pointer',
                color: activeTab === tab.id ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === tab.id ? 700 : 400, fontSize: 13 }}>
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
            {allDepts.map(dept => (
              <DeptCard key={dept} dept={dept}
                score={deptScores[dept] ?? 85}
                alerts={alerts} />
            ))}
          </div>
          {/* Recent Critical Alerts Preview */}
          {criticalCount > 0 && (
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1f2937', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Siren size={16} color="#ef4444" weight="fill" /> Critical Alerts Requiring Immediate Attention
              </h3>
              {alerts.filter(a => a.severity === 'critical').slice(0, 3).map(alert => (
                <AlertCard key={alert._id} alert={alert}
                  onResolve={handleResolve} onSnooze={handleSnooze} onEscalate={handleEscalate} />
              ))}
              {criticalCount > 3 && (
                <button onClick={() => setActiveTab('alerts')}
                  style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  View all {criticalCount} critical alerts →
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'alerts' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['all', 'critical', 'warning', 'info'].map(f => (
              <button key={f} onClick={() => setAlertFilter(f)}
                style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid',
                  borderColor: alertFilter === f ? '#2563eb' : '#d1d5db',
                  background: alertFilter === f ? '#2563eb' : '#fff',
                  color: alertFilter === f ? '#fff' : '#374151',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                {f === 'all' ? `All (${alerts.length})` : `${f} (${alerts.filter(a => a.severity === f).length})`}
              </button>
            ))}
          </div>
          {filteredAlerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
              <CheckCircle size={40} color="#22c55e" weight="fill" style={{ marginBottom: 12 }} />
              <p style={{ fontSize: 14, fontWeight: 600 }}>No active alerts</p>
              <p style={{ fontSize: 12 }}>All departments are operating normally</p>
            </div>
          ) : (
            filteredAlerts.map(alert => (
              <AlertCard key={alert._id} alert={alert}
                onResolve={handleResolve} onSnooze={handleSnooze} onEscalate={handleEscalate} />
            ))
          )}
        </div>
      )}

      {activeTab === 'reports' && (
        <div style={{ display: 'grid', gridTemplateColumns: selectedReport ? '1fr 1fr' : '1fr', gap: 20 }}>
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={() => generateReport('daily')} disabled={generatingReport}
                style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#3b82f6',
                  color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={13} /> Daily Report
              </button>
              <button onClick={() => generateReport('weekly')} disabled={generatingReport}
                style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#8b5cf6',
                  color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={13} /> Weekly Report
              </button>
            </div>
            {generatingReport && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                <Spinner size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
                <p>AI is generating your report using live ERP data...</p>
              </div>
            )}
            {reports.length === 0 && !generatingReport ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                <FileText size={40} color="#d1d5db" weight="fill" style={{ marginBottom: 12 }} />
                <p style={{ fontSize: 14 }}>No reports yet. Generate your first report!</p>
              </div>
            ) : (
              reports.map(r => <ReportCard key={r._id} report={r} onView={setSelectedReport} />)
            )}
          </div>
          {selectedReport && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, position: 'sticky', top: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1f2937' }}>{selectedReport.title}</h3>
                <button onClick={() => setSelectedReport(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  <X size={18} />
                </button>
              </div>
              {selectedReport.metrics && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: 'Health Score', value: `${selectedReport.metrics.avg_health_score}/100` },
                    { label: 'Open Tickets', value: selectedReport.metrics.open_tickets },
                    { label: 'New Customers', value: selectedReport.metrics.new_customers_today },
                    { label: 'Staff Present', value: `${selectedReport.metrics.staff_present}/${selectedReport.metrics.total_staff}` },
                    { label: 'Resolved Today', value: selectedReport.metrics.resolved_today },
                  ].map((m, i) => (
                    <div key={i} style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }}>
                      <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>{m.value}</p>
                      <p style={{ margin: 0, fontSize: 10, color: '#9ca3af' }}>{m.label}</p>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 14, maxHeight: 400, overflowY: 'auto' }}>
                <pre style={{ margin: 0, fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontFamily: 'inherit' }}>
                  {selectedReport.summary}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'nudges' && (
        <div>
          {/* Nudge header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>🤖 AI Smart Nudges</h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>AI-generated reminders for responsible teams to take action</p>
            </div>
            <button onClick={fetchNudges} disabled={nudgeLoading}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <ArrowClockwise size={14} style={nudgeLoading ? { animation: 'spin 1s linear infinite' } : {}} />
              {nudgeLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {nudgeLoading && (
            <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
              <Robot size={40} color="#2563eb" weight="fill" style={{ animation: 'pulse 2s infinite', marginBottom: 12 }} />
              <p>AI is scanning all systems for action items...</p>
            </div>
          )}

          {!nudgeLoading && downNudges && (
            <div style={{ marginBottom: 32 }}>
              {/* Down Alert Nudges */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 4, height: 20, background: '#ef4444', borderRadius: 2 }} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>📡 Network Down — Engineer Nudges</h3>
                <span style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                  {downNudges.total} boxes
                </span>
                <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                  {downNudges.critical} CRITICAL
                </span>
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                  { label: 'Critical (≥48h)', count: downNudges.critical, color: '#ef4444', bg: '#fef2f2' },
                  { label: 'High (≥24h)', count: downNudges.high, color: '#f97316', bg: '#fff7ed' },
                  { label: 'Medium (≥8h)', count: (downNudges.nudges||[]).filter(n=>n.urgency==='medium').length, color: '#eab308', bg: '#fefce8' },
                  { label: 'Low (<8h)', count: (downNudges.nudges||[]).filter(n=>n.urgency==='low').length, color: '#6b7280', bg: '#f9fafb' },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}33`, borderRadius: 10, padding: '10px 16px' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.count}</div>
                    <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(downNudges.nudges || []).map((nudge, i) => {
                  const urgencyColors = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#6b7280' };
                  const urgencyBg = { critical: '#fef2f2', high: '#fff7ed', medium: '#fefce8', low: '#f9fafb' };
                  const uc = urgencyColors[nudge.urgency] || '#6b7280';
                  const ub = urgencyBg[nudge.urgency] || '#f9fafb';
                  const isExpanded = expandedNudge === 'down_' + i;
                  return (
                    <div key={i} style={{ background: '#fff', border: `1px solid ${uc}44`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: uc, marginTop: 6, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>📦 {nudge.box_name}</span>
                            <span style={{ background: ub, color: uc, border: `1px solid ${uc}44`, padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{nudge.urgency_label}</span>
                            <span style={{ background: '#f1f5f9', color: '#475569', padding: '1px 8px', borderRadius: 20, fontSize: 11 }}>⏱ {nudge.duration_label}</span>
                            <span style={{ background: '#fef2f2', color: '#dc2626', padding: '1px 8px', borderRadius: 20, fontSize: 11 }}>👥 {nudge.impacted_count} users</span>
                            {nudge.township && nudge.township !== 'Other' && (
                              <span style={{ color: '#94a3b8', fontSize: 11 }}>📍 {nudge.township}</span>
                            )}
                          </div>
                          <div style={{ background: ub, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 8 }}>
                            {nudge.nudge_message}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, color: '#64748b' }}>
                              👷 {nudge.engineers.length > 0 ? nudge.engineers.join(', ') : 'Unassigned'}
                            </span>
                            <button onClick={() => setExpandedNudge(isExpanded ? null : 'down_' + i)}
                              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
                              {isExpanded ? '▲ Hide' : '▼ Tickets'}
                            </button>
                          </div>
                          {isExpanded && (
                            <div style={{ marginTop: 10, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 12 }}>
                              <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>Ticket IDs:</div>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {nudge.ticket_ids.map(tid => (
                                  <span key={tid} style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: 6, fontFamily: 'monospace', color: '#475569' }}>{tid}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!nudgeLoading && hrNudges && (
            <div style={{ marginBottom: 32 }}>
              {/* HR Nudges */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 4, height: 20, background: '#0ea5e9', borderRadius: 2 }} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>👥 HR — Attendance, Late & Leave Balance Nudges</h3>
                <span style={{ background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                  {hrNudges.summary?.total_employees || 0} employees
                </span>
                <span style={{ background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', padding: '2px 10px', borderRadius: 20, fontSize: 12 }}>
                  {hrNudges.month} · {hrNudges.summary?.working_days_so_far || 0} working days
                </span>
                {!hrNudges.summary?.hik_available && (
                  <span style={{ background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047', padding: '2px 10px', borderRadius: 20, fontSize: 11 }}>
                    ⚠️ Hikvision offline — leave data only
                  </span>
                )}
              </div>
              {/* Summary pills */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                  { label: 'High Absent', count: hrNudges.summary?.high_absent_count || 0, color: '#ef4444', bg: '#fef2f2' },
                  { label: 'Frequent Late', count: hrNudges.summary?.frequent_late_count || 0, color: '#f97316', bg: '#fff7ed' },
                  { label: 'CL Exhausted', count: hrNudges.summary?.cl_exhausted_count || 0, color: '#eab308', bg: '#fefce8' },
                  { label: 'Unauth Absent', count: hrNudges.summary?.high_ca_count || 0, color: '#8b5cf6', bg: '#faf5ff' },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}33`, borderRadius: 10, padding: '10px 16px', minWidth: 80 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.count}</div>
                    <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {/* Nudge cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {(hrNudges.nudges || []).map((nudge, i) => {
                  const urgencyColors = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
                  const urgencyBg = { critical: '#fef2f2', high: '#fff7ed', medium: '#fefce8', low: '#f0fdf4' };
                  const uc = urgencyColors[nudge.urgency] || '#6b7280';
                  const ub = urgencyBg[nudge.urgency] || '#f9fafb';
                  const isExpanded = expandedNudge === 'hr_' + i;
                  return (
                    <div key={i} style={{ background: '#fff', border: `1px solid ${uc}44`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                      <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{nudge.title}</span>
                          <span style={{ background: ub, color: uc, border: `1px solid ${uc}44`, padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{nudge.urgency_label}</span>
                          <span style={{ background: '#f1f5f9', color: '#475569', padding: '1px 8px', borderRadius: 20, fontSize: 11 }}>{nudge.count} employees</span>
                        </div>
                        <div style={{ background: ub, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 8 }}>
                          {nudge.nudge_message}
                        </div>
                        <button onClick={() => setExpandedNudge(isExpanded ? null : 'hr_' + i)}
                          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: '#475569' }}>
                          {isExpanded ? '▲ Hide' : '▼ View Employees'}
                        </button>
                        {isExpanded && nudge.sample_employees && (
                          <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 500 }}>
                              <thead>
                                <tr style={{ background: '#f1f5f9' }}>
                                  {['Name', 'Department', 'Position', 'Present', 'Absent', 'Absent%', 'Late'].map(h => (
                                    <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {nudge.sample_employees.map((e, j) => (
                                  <tr key={j} style={{ borderTop: '1px solid #e2e8f0', background: j % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <td style={{ padding: '7px 10px', color: '#374151', fontWeight: 600 }}>{e.name}</td>
                                    <td style={{ padding: '7px 10px', color: '#64748b' }}>{e.department || '—'}</td>
                                    <td style={{ padding: '7px 10px', color: '#64748b' }}>{e.position || '—'}</td>
                                    <td style={{ padding: '7px 10px', color: '#22c55e', fontWeight: 700 }}>{e.days_present ?? '—'}</td>
                                    <td style={{ padding: '7px 10px', color: '#ef4444', fontWeight: 700 }}>{e.days_absent ?? '—'}</td>
                                    <td style={{ padding: '7px 10px' }}>
                                      {e.absent_rate !== undefined ? (
                                        <span style={{ color: e.absent_rate >= 50 ? '#dc2626' : e.absent_rate >= 30 ? '#f97316' : '#374151', fontWeight: 700 }}>
                                          {e.absent_rate}%
                                        </span>
                                      ) : '—'}
                                    </td>
                                    <td style={{ padding: '7px 10px', color: e.late_count >= 5 ? '#dc2626' : e.late_count >= 3 ? '#f97316' : '#374151', fontWeight: e.late_count >= 3 ? 700 : 400 }}>
                                      {e.late_count ?? e.CA_total ?? '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Leave Balance Table */}
              {hrNudges.leave_balances && hrNudges.leave_balances.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>📋 Leave Balance — {hrNudges.month}</span>
                    <span style={{ background: '#f0f9ff', color: '#0369a1', padding: '1px 8px', borderRadius: 20, fontSize: 11 }}>{hrNudges.leave_balances.length} employees</span>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 600 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {['Name', 'Dept', 'CL Used/Quota', 'CL Left', 'AL Used/Quota', 'AL Left', 'Unauth (CA)', 'Day Off'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {hrNudges.leave_balances.map((e, j) => (
                          <tr key={j} style={{ borderTop: '1px solid #f1f5f9', background: j % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '7px 12px', color: '#374151', fontWeight: 600 }}>{e.name}</td>
                            <td style={{ padding: '7px 12px', color: '#64748b' }}>{e.department || '—'}</td>
                            <td style={{ padding: '7px 12px', color: '#64748b' }}>{e.CL_used}/{e.CL_quota}</td>
                            <td style={{ padding: '7px 12px' }}>
                              <span style={{ color: e.CL_remaining <= 0 ? '#dc2626' : e.CL_remaining <= 2 ? '#f97316' : '#22c55e', fontWeight: 700 }}>
                                {e.CL_remaining}
                              </span>
                            </td>
                            <td style={{ padding: '7px 12px', color: '#64748b' }}>{e.AL_used}/{e.AL_quota}</td>
                            <td style={{ padding: '7px 12px' }}>
                              <span style={{ color: e.AL_remaining <= 0 ? '#dc2626' : e.AL_remaining <= 3 ? '#f97316' : '#22c55e', fontWeight: 700 }}>
                                {e.AL_remaining}
                              </span>
                            </td>
                            <td style={{ padding: '7px 12px', color: e.CA_total >= 2 ? '#dc2626' : '#64748b', fontWeight: e.CA_total >= 2 ? 700 : 400 }}>{e.CA_total}</td>
                            <td style={{ padding: '7px 12px', color: '#64748b' }}>{e.Off_total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {!nudgeLoading && salesNudges && (
            <div style={{ marginBottom: 32 }}>
              {/* Sales / Installation Nudges */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 4, height: 20, background: '#8b5cf6', borderRadius: 2 }} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>🛒 Sales / Installation — Pending & Cancel Nudges</h3>
                <span style={{ background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                  {salesNudges.total_pending} pending
                </span>
                {salesNudges.total_cancelled_30d > 0 && (
                  <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                    {salesNudges.total_cancelled_30d} cancelled (30d)
                  </span>
                )}
              </div>
              {/* Summary pills */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                  { label: 'Critical >14d', count: salesNudges.critical_pending, color: '#ef4444', bg: '#fef2f2' },
                  { label: 'High 7-14d', count: salesNudges.high_pending, color: '#f97316', bg: '#fff7ed' },
                  { label: 'Medium 3-7d', count: salesNudges.medium_pending, color: '#eab308', bg: '#fefce8' },
                  { label: 'New <3d', count: salesNudges.low_pending, color: '#22c55e', bg: '#f0fdf4' },
                  { label: 'Cancelled 30d', count: salesNudges.total_cancelled_30d, color: '#6b7280', bg: '#f9fafb' },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}33`, borderRadius: 10, padding: '10px 16px' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.count}</div>
                    <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(salesNudges.nudges || []).map((nudge, i) => {
                  const urgencyColors = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
                  const urgencyBg = { critical: '#fef2f2', high: '#fff7ed', medium: '#fefce8', low: '#f0fdf4' };
                  const uc = urgencyColors[nudge.urgency] || '#6b7280';
                  const ub = urgencyBg[nudge.urgency] || '#f9fafb';
                  const isExpanded = expandedNudge === 'sales_' + i;
                  return (
                    <div key={i} style={{ background: '#fff', border: `1px solid ${uc}44`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                      <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{nudge.title}</span>
                          <span style={{ background: ub, color: uc, border: `1px solid ${uc}44`, padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{nudge.urgency_label}</span>
                          <span style={{ background: '#f1f5f9', color: '#475569', padding: '1px 8px', borderRadius: 20, fontSize: 11 }}>{nudge.count} cases</span>
                        </div>
                        <div style={{ background: ub, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 8 }}>
                          {nudge.nudge_message}
                        </div>
                        {/* By-sales breakdown */}
                        {nudge.by_sales && nudge.by_sales.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            {nudge.by_sales.map((s, j) => (
                              <span key={j} style={{ background: '#f1f5f9', color: '#475569', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                                👤 {s.name}: {s.count}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Cancel reasons for cancelled analysis */}
                        {nudge.cancel_reasons && nudge.cancel_reasons.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            {nudge.cancel_reasons.map((r, j) => (
                              <span key={j} style={{ background: '#fef2f2', color: '#dc2626', padding: '2px 10px', borderRadius: 20, fontSize: 11 }}>
                                ❌ {r.reason}: {r.count}
                              </span>
                            ))}
                          </div>
                        )}
                        <button onClick={() => setExpandedNudge(isExpanded ? null : 'sales_' + i)}
                          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {isExpanded ? '▲ Hide' : '▼ View Customers'}
                        </button>
                        {isExpanded && nudge.sample_records && (
                          <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 600 }}>
                              <thead>
                                <tr style={{ background: '#f1f5f9' }}>
                                  {nudge.category === 'cancelled_analysis'
                                    ? ['Sale ID', 'Customer', 'Phone', 'Township', 'Sales', 'Cancel Reason', 'Registered', 'Cancelled'].map(h => (
                                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                      ))
                                    : ['Sale ID', 'Customer', 'Phone', 'Township', 'Bandwidth', 'Sales', 'Wait Days', 'Registered', 'Remark'].map(h => (
                                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                      ))
                                  }
                                </tr>
                              </thead>
                              <tbody>
                                {nudge.sample_records.map((r, j) => (
                                  <tr key={j} style={{ borderTop: '1px solid #e2e8f0', background: j % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    {nudge.category === 'cancelled_analysis' ? (
                                      <>
                                        <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: '#1d4ed8', whiteSpace: 'nowrap' }}>{r.sale_id}</td>
                                        <td style={{ padding: '7px 10px', color: '#374151' }}>{r.customer_name}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.phone}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{r.township}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{r.sales_name}</td>
                                        <td style={{ padding: '7px 10px', color: '#dc2626', fontWeight: 600 }}>{r.cancel_reason}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.created_at}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.cancelled_at}</td>
                                      </>
                                    ) : (
                                      <>
                                        <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: '#1d4ed8', whiteSpace: 'nowrap' }}>{r.sale_id}</td>
                                        <td style={{ padding: '7px 10px', color: '#374151' }}>{r.customer_name}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.phone}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{r.township}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{r.bandwidth}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b' }}>{r.sales_name}</td>
                                        <td style={{ padding: '7px 10px' }}>
                                          <span style={{ color: r.wait_days >= 14 ? '#dc2626' : r.wait_days >= 7 ? '#f97316' : '#374151', fontWeight: 700 }}>
                                            {r.wait_days}d
                                          </span>
                                        </td>
                                        <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.created_at}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.remark}</td>
                                      </>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!nudgeLoading && billingNudges && (
            <div>
              {/* Billing Nudges */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 4, height: 20, background: '#f59e0b', borderRadius: 2 }} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>💰 Billing — Collection Nudges</h3>
                <span style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                  {billingNudges.expired_count} expired
                </span>
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                  { label: 'Expired', count: billingNudges.expired_count, color: '#ef4444', bg: '#fef2f2' },
                  { label: 'Expiring 7d', count: billingNudges.expiring_7d, color: '#f97316', bg: '#fff7ed' },
                  { label: 'Expiring 30d', count: billingNudges.expiring_30d, color: '#eab308', bg: '#fefce8' },
                  { label: 'Est. Loss/mo', count: String(billingNudges.summary && billingNudges.summary.estimated_monthly_loss_mmk ? ((billingNudges.summary.estimated_monthly_loss_mmk / 1000000).toFixed(1) + 'M') : '0M'), color: '#7c3aed', bg: '#f5f3ff' },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}33`, borderRadius: 10, padding: '10px 16px' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.count}</div>
                    <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(billingNudges.nudges || []).map((nudge, i) => {
                  const urgencyColors = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
                  const urgencyBg = { critical: '#fef2f2', high: '#fff7ed', medium: '#fefce8', low: '#f0fdf4' };
                  const uc = urgencyColors[nudge.urgency] || '#6b7280';
                  const ub = urgencyBg[nudge.urgency] || '#f9fafb';
                  const isExpanded = expandedNudge === 'bill_' + i;
                  return (
                    <div key={i} style={{ background: '#fff', border: `1px solid ${uc}44`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                      <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{nudge.title}</span>
                          <span style={{ background: ub, color: uc, border: `1px solid ${uc}44`, padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{nudge.urgency_label}</span>
                          <span style={{ background: '#f1f5f9', color: '#475569', padding: '1px 8px', borderRadius: 20, fontSize: 11 }}>{nudge.count} customers</span>
                        </div>
                        <div style={{ background: ub, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 8 }}>
                          {nudge.nudge_message}
                        </div>
                        <button onClick={() => setExpandedNudge(isExpanded ? null : 'bill_' + i)}
                          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {isExpanded ? '▲ Hide Samples' : '▼ View Sample Customers'}
                        </button>
                        {isExpanded && nudge.sample_users && (
                          <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ background: '#f1f5f9' }}>
                                  {['Username', 'Name', 'Phone', 'Expiry', 'Days'].map(h => (
                                    <th key={h} style={{ padding: '7px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {nudge.sample_users.map((u, j) => (
                                  <tr key={j} style={{ borderTop: '1px solid #e2e8f0' }}>
                                    <td style={{ padding: '7px 12px', fontFamily: 'monospace', color: '#1d4ed8' }}>{u.username}</td>
                                    <td style={{ padding: '7px 12px', color: '#374151' }}>{u.name || '—'}</td>
                                    <td style={{ padding: '7px 12px', color: '#64748b' }}>{u.phone}</td>
                                    <td style={{ padding: '7px 12px', color: '#64748b' }}>{u.expiration}</td>
                                    <td style={{ padding: '7px 12px' }}>
                                      <span style={{ color: u.days_left < 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                                        {u.days_left < 0 ? `${Math.abs(u.days_left)}d ago` : `${u.days_left}d`}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!nudgeLoading && !downNudges && !billingNudges && (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <Lightning size={40} color="#d1d5db" weight="fill" style={{ marginBottom: 12 }} />
              <p style={{ fontSize: 14 }}>Click Refresh to load AI nudges</p>
            </div>
          )}
        </div>
      )}
      {activeTab === 'chat' && (
        <div style={{ height: 'calc(100vh - 280px)', minHeight: 500 }}>
          <ChatPanel token={token} />
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
}
